import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Search,
  Plus,
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  Calendar,
} from "lucide-react";
import { GetUserOwnEvents, DeleteEvent } from "../../redux/actions/Events";

const formatMoney = (value) =>
  `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function getEventName(event) {
  const n = event?.eventName ?? event?.name;
  if (typeof n === "string") return n;
  if (typeof n === "object" && n !== null) {
    return n.name || n.title || "Unnamed Event";
  }
  return "Unnamed Event";
}

function getEventLocation(event) {
  const loc = event?.location;
  if (!loc) return "";
  if (typeof loc === "string") return loc;
  if (typeof loc === "object") {
    return loc.address || loc.name || loc.city || "";
  }
  return String(loc);
}

/** Category may be a string or API object `{ id, name, slug, ... }`. */
function getEventCategory(event) {
  const cat = event?.category;
  if (!cat) return "";
  if (typeof cat === "string") return cat;
  if (typeof cat === "object" && cat !== null) {
    return cat.name || cat.slug || cat.title || "";
  }
  return "";
}

function formatEventDate(event) {
  const raw = event?.startDate || event?.date || event?.createdAt;
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatEventTime(event) {
  const start = event?.startTime;
  const end = event?.endTime;
  if (!start && !end) return null;
  if (start && end) return `${start} – ${end}`;
  return start || end;
}

function getStatusLabel(status) {
  const s = (status || "active").toLowerCase();
  if (s === "active" || s === "published" || s === "live") return "Published";
  if (s === "draft") return "Draft";
  if (s === "completed") return "Completed";
  if (s === "cancelled") return "Cancelled";
  return status || "Active";
}

function isPublishedStatus(status) {
  const s = (status || "active").toLowerCase();
  return s === "active" || s === "published" || s === "live";
}

export default function UserEvents() {
  const dispatch = useDispatch();
  const { userCreatedEvents, userCreatedEventsLoading: loading } = useSelector(
    (state) => state.events
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => {
    dispatch(GetUserOwnEvents());
  }, [dispatch]);

  const events = Array.isArray(userCreatedEvents) ? userCreatedEvents : [];

  const filteredEvents = useMemo(() => {
    let list = events;

    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      list = list.filter((event) => {
        const name = getEventName(event).toLowerCase();
        const location = getEventLocation(event).toLowerCase();
        const category = getEventCategory(event).toLowerCase();
        return name.includes(q) || location.includes(q) || category.includes(q);
      });
    }

    if (statusFilter !== "all") {
      list = list.filter((event) => {
        const status = (event?.status || "active").toLowerCase();
        if (statusFilter === "active") {
          return status === "active" || status === "published" || status === "live";
        }
        return status === statusFilter.toLowerCase();
      });
    }

    return list;
  }, [events, searchTerm, statusFilter]);

  const handleDeleteEvent = async (eventId, eventName) => {
    if (window.confirm(`Delete "${eventName}"? This cannot be undone.`)) {
      try {
        await dispatch(DeleteEvent(eventId));
        dispatch(GetUserOwnEvents());
      } catch (error) {
        console.error("Error deleting event:", error);
        alert("Failed to delete event. Please try again.");
      }
    }
    setOpenMenuId(null);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
      {/* Header — matches new-ui /organizer/events */}
      <div className="border-b border-[#1E293B]/60 bg-[#060D1B] shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
          <div>
            <h1 className="text-[22px] sm:text-[28px] text-white" style={{ fontWeight: 800 }}>
              My Events
            </h1>
            <p className="text-white/30 text-[13px] mt-1">
              Manage all your events in one place
            </p>
          </div>
          <Link
            to="/create-event/form"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-blue-500 text-white text-[13px] hover:bg-blue-600 transition-colors shadow-lg shadow-blue-500/20 shrink-0"
            style={{ fontWeight: 700 }}
          >
            <Plus className="w-4 h-4" />
            Create Event
          </Link>
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8">
        {/* Search + status filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/3 -translate-y-1/2 w-5 h-5 text-white/25" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-[14px] placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="sm:w-48 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 cursor-pointer"
          >
            <option value="all" className="bg-[#0A1628]">
              All Status
            </option>
            <option value="active" className="bg-[#0A1628]">
              Published
            </option>
            <option value="draft" className="bg-[#0A1628]">
              Draft
            </option>
            <option value="completed" className="bg-[#0A1628]">
              Completed
            </option>
            <option value="cancelled" className="bg-[#0A1628]">
              Cancelled
            </option>
          </select>
        </div>

        <p className="text-[12px] text-white/30 mb-4">
          {loading ? "Loading events…" : `${filteredEvents.length} event${filteredEvents.length === 1 ? "" : "s"}`}
        </p>

        {loading ? (
          <div className="flex justify-center items-center py-24">
            <div className="w-10 h-10 rounded-full border-2 border-blue-500/30 border-t-blue-500 animate-spin" />
          </div>
        ) : filteredEvents.length > 0 ? (
          <div className="rounded-2xl bg-[#0A1628] border border-[#1E293B]/60 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1E293B]/40 bg-white/[0.02]">
                    <th
                      className="text-left px-6 py-4 text-[11px] text-white/30 uppercase tracking-wider"
                      style={{ fontWeight: 700 }}
                    >
                      Event
                    </th>
                    <th
                      className="text-left px-6 py-4 text-[11px] text-white/30 uppercase tracking-wider"
                      style={{ fontWeight: 700 }}
                    >
                      Date & Time
                    </th>
                    <th
                      className="text-left px-6 py-4 text-[11px] text-white/30 uppercase tracking-wider"
                      style={{ fontWeight: 700 }}
                    >
                      Status
                    </th>
                    <th
                      className="text-left px-6 py-4 text-[11px] text-white/30 uppercase tracking-wider"
                      style={{ fontWeight: 700 }}
                    >
                      Sold
                    </th>
                    <th
                      className="text-left px-6 py-4 text-[11px] text-white/30 uppercase tracking-wider"
                      style={{ fontWeight: 700 }}
                    >
                      Revenue
                    </th>
                    <th
                      className="text-left px-6 py-4 text-[11px] text-white/30 uppercase tracking-wider"
                      style={{ fontWeight: 700 }}
                    >
                      Views
                    </th>
                    <th className="px-6 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.map((event) => {
                    const id = event._id;
                    const name = getEventName(event);
                    const location = getEventLocation(event);
                    const statusLabel = getStatusLabel(event.status);
                    const published = isPublishedStatus(event.status);
                    const timeStr = formatEventTime(event);
                    const sold = event.ticketsSold ?? 0;
                    const total = event.totalTickets;
                    const revenue = event.totalRevenue ?? event.revenue ?? 0;
                    const views = event.views ?? null;

                    return (
                      <tr
                        key={id}
                        className="border-b border-[#1E293B]/20 last:border-b-0 hover:bg-white/[0.02] transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="min-w-[200px]">
                            <div className="text-[13px] text-white truncate" style={{ fontWeight: 600 }}>
                              {name}
                            </div>
                            {location && (
                              <div className="text-[11px] text-white/35 truncate">{location}</div>
                            )}
                            {getEventCategory(event) && (
                              <div className="text-[10px] text-white/20 truncate">{getEventCategory(event)}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-[13px] text-white/70">{formatEventDate(event)}</div>
                          {timeStr && (
                            <div className="text-[11px] text-white/30">{timeStr}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] ${
                              published
                                ? "bg-[#E52324]/10 text-[#E52324]"
                                : statusLabel === "Draft"
                                  ? "bg-amber-500/10 text-amber-400"
                                  : "bg-white/5 text-white/50"
                            }`}
                            style={{ fontWeight: 700 }}
                          >
                            {statusLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-[13px] text-white" style={{ fontWeight: 600 }}>
                            {sold}
                          </div>
                          <div className="text-[10px] text-white/25">
                            of {total != null && total !== "" ? total : "∞"}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-[13px] text-white" style={{ fontWeight: 600 }}>
                          {formatMoney(revenue)}
                        </td>
                        <td className="px-6 py-4 text-[13px] text-white/60">
                          {views == null ? <span title="Page views aren't tracked yet">Not tracked</span> : Number(views).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 relative">
                          <button
                            type="button"
                            onClick={() => setOpenMenuId(openMenuId === id ? null : id)}
                            className="p-2 hover:bg-white/5 rounded-lg transition-colors text-white/30 hover:text-white/60"
                            aria-label="Event actions"
                          >
                            <MoreVertical className="w-5 h-5" />
                          </button>
                          {openMenuId === id && (
                            <>
                              <button
                                type="button"
                                className="fixed inset-0 z-10"
                                onClick={() => setOpenMenuId(null)}
                                aria-label="Close menu"
                              />
                              <div className="absolute right-6 top-full mt-1 z-20 min-w-[140px] py-1 rounded-xl bg-[#111827] border border-[#1E293B] shadow-xl">
                                <button
                                  type="button"
                                  onClick={() => {
                                    window.open(`/event/${id}`, "_blank");
                                    setOpenMenuId(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-[12px] text-white/70 hover:bg-white/5 hover:text-white"
                                >
                                  <Eye className="w-3.5 h-3.5" /> View
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    window.open(`/edit-event/${id}`, "_blank");
                                    setOpenMenuId(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-[12px] text-white/70 hover:bg-white/5 hover:text-white"
                                >
                                  <Pencil className="w-3.5 h-3.5" /> Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteEvent(id, name)}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-[12px] text-red-400 hover:bg-red-500/10"
                                >
                                  <Trash2 className="w-3.5 h-3.5" /> Delete
                                </button>
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl bg-[#0A1628] border border-[#1E293B]/60 py-16 px-6 text-center">
            <Calendar className="w-12 h-12 text-white/15 mx-auto mb-4" />
            <h3 className="text-[16px] text-white mb-2" style={{ fontWeight: 700 }}>
              {searchTerm || statusFilter !== "all"
                ? "No events match your filters"
                : "No events created yet"}
            </h3>
            <p className="text-[13px] text-white/35 mb-6 max-w-sm mx-auto">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your search or filter criteria."
                : "Create your first event to start selling tickets."}
            </p>
            <Link
              to="/create-event/form"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-500 text-white text-[13px] hover:bg-blue-600 transition-colors"
              style={{ fontWeight: 700 }}
            >
              <Plus className="w-4 h-4" />
              Create Your First Event
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
